import DOMPurify from 'dompurify';
import React, { Component, type SyntheticEvent } from 'react';
import { IoInformationCircle } from 'react-icons/io5';
import { ConfigContext } from '../context/ConfigContext';
import analyticsService from '../services/analyticsService';
import type { MessageBoxProps, MessageBoxState } from '../types/ChatWindow';
import type { MenuEntry } from '../types/configs';
import AttachmentButton from './AttachmentButton';
import CameraButton from './CameraButton';
import ChatEnterButton from './ChatEnterButton';
import ChatTextArea from './ChatTextArea';
import ChatEmojiPicker from './EmojiPicker';
import Menu from './Menu';
import MenuButton from './MenuButton';

class MessageBox extends Component<MessageBoxProps, MessageBoxState> {
  static contextType = ConfigContext;
  context!: React.ContextType<typeof ConfigContext>;

  constructor(props: MessageBoxProps) {
    super(props);
    this.spanReference = React.createRef();
    this.state = {
      text: '',
      textareaContent: '',
      showMenu: false,
      showWaitingForTheBotResponseMessage: false,
    };
  }
  spanReference: React.RefObject<HTMLSpanElement | null>;

  public handleSubmit = (value: string, callback: (...args: any[]) => void) => {
    const { attachments, setAttachments } = this.props;
    // Trim the value to remove any leading or trailing whitespace
    const trimmedValue = value?.trim();

    if (trimmedValue || attachments.length > 0) {
      callback({ value: trimmedValue, attachments });

      if (trimmedValue) {
        this.clearInput();
      }

      if (attachments.length > 0) {
        setAttachments([]);
      }
    }
  };

  private clearInput() {
    if (this.spanReference?.current) {
      this.spanReference.current.innerText = '';
    }
    this.setState({ text: '', textareaContent: '' });
  }

  public handleChange = (event: SyntheticEvent) => {
    const target = event.target as HTMLSpanElement;

    this.setState({
      text: target.innerText || '',
    });
  };

  public shouldSubmit = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    const { text } = this.state;
    const { submitHandler, setSendChatStatus, changeChatActivityStatus } =
      this.props;
    const { configId } = this.context;
    const code = event.keyCode;

    if (code === 13) {
      // Enter sends the message
      event.preventDefault();
      if (text.trim() || this.props.attachments.length > 0) {
        try {
          // Sanitize the text input to prevent XSS attacks
          const cleanText = DOMPurify.sanitize(text);

          // Attempt to handle the submit action
          this.handleSubmit(cleanText, submitHandler);

          // Send user status update to the server
          setSendChatStatus({ status: 'online' }, configId);
          changeChatActivityStatus('online');
        } catch (_error) {
          // If an error occurs in the handleSubmit callback, handle it here
          this.setState({ showWaitingForTheBotResponseMessage: true });

          setTimeout(() => {
            this.setState({ showWaitingForTheBotResponseMessage: false });
          }, 2000); // Hide the error message after 2 seconds

          console.log('Waiting for the bot response');
        }
      }
    }
  };

  // TODO: Supply proper type
  public appendEmoji = (emoji: { native: string }) => {
    const { native } = emoji;

    if (this.spanReference?.current) {
      this.spanReference.current.innerText += native;
    }

    this.setState((prevState) => ({
      text: prevState.text + native,
    }));
  };

  toggleMenuState = () => {
    const { showMenu } = this.state;
    this.setState({
      showMenu: !showMenu,
    });
  };

  handleMenuClick = (menuEntry: MenuEntry) => {
    const { submitHandler } = this.props;

    if (menuEntry.type === 'webLink') {
      const { payload: url } = menuEntry;
      const win = window.open(url, '_blank');
      if (win != null) {
        win.focus();
      }
    }
    if (menuEntry.type === 'payload') {
      const { payload, title } = menuEntry;
      const button = {
        title,
        // * prefix payload needs to be applied for buttons to work
        payload: `payload${payload}`,
      };

      this.setState((state) => ({
        ...state,
        showMenu: false,
      }));

      submitHandler({ value: button });
    }
  };

  onMediaAttached = (media) => {
    const { attachments, setAttachments } = this.props;
    const newAttachments = attachments.concat(media);
    setAttachments(newAttachments);
  };

  onCameraUsed = (media) => {
    const { submitHandler } = this.props;
    submitHandler({ attachments: media });
  };

  onCameraError = (error) => {
    console.log(error);
  };

  onMediaAttachedError = (error) => {
    console.log(error);
  };

  public render() {
    const { text, textareaContent, showMenu } = this.state;
    const { submitHandler, allowTextInput } = this.props;
    const { attachments, messages } = this.props;

    const { useAttachment } = this.context;

    const {
      scheme: {
        header: {
          windowColor,
          messageBarColor,
          messageBarTextColor,
          headerColor,
        },
        message: { submitButtonColor },
      },
      scheme,
      menuTemplate,
      messagePlaceholder,
      waitingForTheBotResponseMessage,
    } = this.context;

    const useCamera = useAttachment && analyticsService.isMobileDevice();

    const showMenuButton =
      menuTemplate && Array.isArray(menuTemplate) && menuTemplate.length > 0;

    const messageBoxStyle = {
      backgroundColor: windowColor,
      '--message-bar-text-color': messageBarTextColor || '#4D4D4D',
      borderColor: headerColor,
    };

    return (
      <>
        {this.state.showWaitingForTheBotResponseMessage && (
          <div className='information-message-container'>
            <IoInformationCircle className='information-message-icon' />
            <span className='information-message-content'>
              {waitingForTheBotResponseMessage}
            </span>
          </div>
        )}

        <div className='message-box-container' style={messageBoxStyle}>
          <div
            className='message-box-row'
            style={{
              backgroundColor: messageBarColor,
            }}>
            <Menu
              scheme={scheme}
              show={showMenu}
              handleMenuClick={this.handleMenuClick}
              menuTemplate={menuTemplate}
            />
            <div className='message-box-input-container'>
              <MenuButton
                scheme={scheme}
                show={showMenuButton}
                toggleMenuState={this.toggleMenuState}
              />
              {/* The input area should only available if it is allowed and there is only one flow message ( in case of the Welcome message the input should be hidden ) */}
              {allowTextInput && !!messages.length && (
                <>
                  <ChatTextArea
                    content={textareaContent}
                    spanRef={this.spanReference}
                    messagePlaceholder={messagePlaceholder}
                    shouldSubmit={this.shouldSubmit}
                    handleChange={this.handleChange}
                  />
                  <div className='message-box-right-buttons-container'>
                    <CameraButton
                      scheme={scheme}
                      show={useCamera}
                      onCameraUsed={this.onCameraUsed}
                      onCameraError={this.onCameraError}
                    />
                    {attachments?.length === 0 && (
                      <AttachmentButton
                        scheme={scheme}
                        show={useAttachment}
                        onMediaAttached={this.onMediaAttached}
                        onMediaAttachedError={this.onMediaAttachedError}
                      />
                    )}
                    <ChatEmojiPicker
                      show={false}
                      onEmojiClick={this.appendEmoji}
                    />
                    <ChatEnterButton
                      submit={() => this.handleSubmit(text, submitHandler)}
                      color={submitButtonColor}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default MessageBox;
